import React, { Component, ReactNode } from 'react';
import { Route } from 'react-router-dom';
import classNames from 'classnames';
import withStyles from 'src/common/withStyles';
import { Helmet } from 'react-helmet-async';
import { connect } from 'react-redux';

import type { ReduxState } from '../../../types/reduxState';
import type { ImmutableBoat } from '../../../types/boat/BoatDetail';
import { TITLE } from '../../../common/constants';
import { PAGES } from '../../constants';
import Layout from '../../../common/components/Layout';
import { getClassNameFor, decorateComponent } from '../../../common/helpers';
import Header from '../../../common/components/Header';
import WizardProgressBar from '../../../common/components/WizardProgress';
import { setLoading, setLoadingComplete } from '../../../common/ducks/loading';
import { getUserLocationCall } from '../../../common/ducks/user';
import { fetchBoat } from '../../../common/ducks/boat';

import s from './Inquiry.module.scss';

const mapStateToProps = ({ boat }: ReduxState) => ({
  boat,
});
const mapDispatchToProps = {
  startLoading: setLoading,
  finishLoading: setLoadingComplete,
  getUserLocation: getUserLocationCall,
  getBoat: fetchBoat,
};

type InquiryRootProps = {
  children: ReactNode;
  match: { params: { boatId?: string }; isExact: boolean; url: string };
  boat: ImmutableBoat;
  startLoading: () => void;
  finishLoading: () => void;
  getUserLocation: () => void;
  getBoat: (boatId: string) => Promise<void>;
};

class InquiryRoot extends Component<InquiryRootProps> {
  // TODO: write some tests for this lifecycle method.
  componentDidMount() {
    const {
      match: { params: { boatId } },
      boat,
      startLoading,
      finishLoading,
      getUserLocation,
      getBoat,
    } = this.props;

    startLoading();
    getUserLocation();

    // Boat in particular is unlikely to matter, since we're coming from the listings
    // page in most cases, but it's good to be defensive.
    if (boatId && (boat.get('id') !== boatId)) {
      getBoat(boatId)
        .then(finishLoading)
        // TODO: implement error handling
        .catch(() => {});
    } else {
      finishLoading();
    }
  }

  render() {
    const {
      children,
      match: { isExact, url },
    } = this.props;
    const classNameModifier = classNames(
      'modal with-blue-gradient-background',
      {
        welcome: isExact,
      },
    );

    return (
      <Layout classNameModifier={classNameModifier}>
        <Header
          withNav={false}
          withSearchBar={false}
          classNameModifier={classNameModifier}
        />
        <div className={getClassNameFor(s, 'modal', classNameModifier)}>
          <Helmet
            defaultTitle={`${TITLE.INQUIRY} | ${TITLE.PROJECT}`}
            titleTemplate={`${TITLE.INQUIRY}: %s | ${TITLE.PROJECT}`}
          />
          {/* Don't render progress bar on welcome & inquiry sent screens */}
          <Route path={[`${url}/${PAGES.DONE}/`, url]} exact>
            {({ match: nonProgressPage }) => (
              !nonProgressPage && (
                <WizardProgressBar
                  routes={[
                    // These are only the pages where we render the progress bar, in
                    // the order that they appear.
                    PAGES.DURATION,
                    PAGES.DATES,
                    PAGES.TIME,
                    PAGES.GROUP,
                    PAGES.CAPTAIN,
                    PAGES.EXPERIENCE,
                    PAGES.EXTRAS,
                    PAGES.CONTACT,
                    PAGES.PASSWORD,
                    PAGES.INSTABOOK,
                    PAGES.MATCH,
                    PAGES.WARNING,
                  ]}
                  classNameModifier="modal"
                />
              )
            )}
          </Route>
          {children}
        </div>
      </Layout>
    );
  }
}

const decorators = [
  withStyles(s),
  connect(mapStateToProps, mapDispatchToProps),
];

export default decorateComponent(InquiryRoot, decorators);
